import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'span',
  'sup',
  'sub',
  'del',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'style'];

/**
 * Map of tag names to Tailwind classes (tw: prefixed) applied after sanitisation
 * so that Tailwind's Preflight reset doesn't strip list markers, spacing, etc.
 */
const TAG_CLASSES: Record<string, string> = {
  UL: 'tw:list-disc tw:pl-6 tw:my-2',
  OL: 'tw:list-decimal tw:pl-6 tw:my-2',
  LI: 'tw:my-1',
  P: 'tw:my-2',
  BLOCKQUOTE: 'tw:border-l-4 tw:border-gray-300 tw:pl-4 tw:my-2 tw:italic',
  A: 'tw:text-blue-600 tw:underline',
  H1: 'tw:text-2xl tw:font-bold tw:my-3',
  H2: 'tw:text-xl tw:font-bold tw:my-2',
  H3: 'tw:text-lg tw:font-semibold tw:my-2',
  H4: 'tw:text-base tw:font-semibold tw:my-1',
};

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  const classes = TAG_CLASSES[node.tagName];
  if (classes) {
    node.setAttribute('class', classes);
  }

  // Force all links to open in a new tab with safe rel attributes
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ['onerror', 'onload'],
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed'],
  });
}

interface SafeRichTextProps {
  html: string | null | undefined;
  className?: string;
}

export function SafeRichText({ html, className }: SafeRichTextProps) {
  const clean = useMemo(() => sanitizeHtml(html ?? ''), [html]);
  return (
    <div
      className={cn(className)}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
